import axios from "axios";
import Swal from "sweetalert2";
import {
  SCHEDULE_ERROR_COPY,
  isShortening,
  daysBetween,
  toDateInputValue,
  toDateTimeLocalValue,
  goLiveError,
} from "./scheduleDates";
import { fmtDateShort } from "./fmtDate";
import { rejectionReasonError } from "./rejectionReason";

const API = process.env.REACT_APP_API;

function getToken() {
  return localStorage.getItem("accessToken");
}

function headers() {
  return { "x-access-token": getToken() };
}

/**
 * Show a confirmation dialog. Returns true if confirmed.
 *
 * If `preConfirm` is supplied, the modal shows an inline loader and stays
 * open until the async work resolves. Errors should call
 * Swal.showValidationMessage from within preConfirm — the modal will then
 * stay open with the message and result.isConfirmed === false.
 */
async function confirmAction({
  title, text, confirmButtonText, isDanger = false, preConfirm,
  // Decision 4 needs a dialog that COLLECTS something (a new launch date).
  // These were not forwarded before; a caller passing them got a dialog with no
  // input and a preConfirm receiving `undefined` — silently, because Swal simply
  // ignores options it is not given. Spread rather than enumerated so the next
  // input-bearing dialog does not hit the same wall.
  ...swalOptions
}) {
  const result = await Swal.fire({
    title,
    text,
    icon: "warning",
    showCancelButton: true,
    showCloseButton: true,
    confirmButtonColor: isDanger ? "#d33" : "#3085d6",
    cancelButtonColor: isDanger ? "#3085d6" : "#d33",
    confirmButtonText,
    cancelButtonText: "Cancel",
    showLoaderOnConfirm: !!preConfirm,
    allowOutsideClick: () => !Swal.isLoading(),
    preConfirm,
    ...swalOptions,
  });
  return result.isConfirmed;
}

function showSuccess(title) {
  Swal.fire({
    title,
    timer: 2500,
    timerProgressBar: true,
    icon: "success",
    confirmButtonText: "OK",
    showCloseButton: true,
  });
}

function showError(error) {
  Swal.fire({
    title: "Error",
    timer: 2500,
    timerProgressBar: true,
    text: error?.response?.data?.message || "An error occurred",
    icon: "error",
    confirmButtonColor: "#f27474",
    confirmButtonText: "OK",
    dangerMode: true,
    showCloseButton: true,
  });
}

// A rejection refused for its reason, by the check before the dialog or by the
// API's 400 REJECTION_REASON_REQUIRED, is shown WITHOUT a timer: the operator
// has to read it and write a reason, not watch it disappear.
function showRejectionReasonRefusal(message) {
  Swal.fire({
    title: "A reason is required",
    text: message,
    icon: "error",
    confirmButtonColor: "#f27474",
    confirmButtonText: "OK",
    showCloseButton: true,
  });
}

/** The API's REJECTION_REASON_REQUIRED message, or null for any other failure. */
function rejectionReasonRefusal(error) {
  const data = error?.response?.data;
  if (data?.code !== "REJECTION_REASON_REQUIRED") return null;
  return data.message || rejectionReasonError("");
}

// C2: both decision endpoints compare the content_revision the admin reviewed
// with the stored one under the campaign lock. 409 (changed since it was
// opened) and 400 (no revision sent) mean the same thing to the operator: what
// is on screen is not what would be decided. No retry of the dialog can fix
// that, so it closes and the panel offers Reload.
const STALE_REVIEW_CODES = ["campaign_changed_since_review", "content_revision_required"];
const STALE_REVIEW_MESSAGE = "This campaign changed after you opened it. Reload to review the latest version.";

/** The API's stale-review message, or null for any other failure. */
function staleReviewRefusal(error) {
  const data = error?.response?.data;
  if (!STALE_REVIEW_CODES.includes(data?.code)) return null;
  return data.message || STALE_REVIEW_MESSAGE;
}

/** "Nov 30, 2099 at 09:30 Toronto time" from the API's wall-clock string. */
function readableLaunch(launchDate) {
  const time = toDateTimeLocalValue(launchDate).slice(11);
  return `${fmtDateShort(launchDate)}${time ? ` at ${time}` : ""} Toronto time`;
}

// ---------------------------------------------------------------------------
// Workflow 1: First-Time Approval (update-campaign-byadmin)
//   superAdmin.controller.js:1830 — status update + slug gen + email, no drafts
// ---------------------------------------------------------------------------

/**
 * How long the read-back after a decision may take. It runs inside the confirm
 * dialog's preConfirm, so an unbounded wait would leave the dialog spinning
 * forever on a decision that is already saved.
 */
export const CAMPAIGN_READ_TIMEOUT_MS = 10000;

/**
 * The row's campaign_status as the server now has it, read back through the
 * console's own campaign read (the one CampaignViewPage loads), or null when it
 * cannot be read within CAMPAIGN_READ_TIMEOUT_MS. update-campaign-byadmin
 * answers only { status, message }, so the outcome of an approval has to be
 * asked for. A read, never a write: safe to repeat.
 */
async function readCampaignStatus(campaignId) {
  const read = (async () => {
    try {
      const res = await axios({
        method: "post",
        url: API + "admin/get-campaigndetailsby-id",
        headers: headers(),
        data: { campaign_id: campaignId, is_deleted: 0 },
        timeout: CAMPAIGN_READ_TIMEOUT_MS,
      });
      const status = Number(res?.data?.data?.[0]?.campaign_status);
      return Number.isFinite(status) ? status : null;
    } catch (error) {
      return null;
    }
  })();
  // axios's own timeout covers a real request; this race also bounds anything
  // that never settles at all.
  let timer;
  const timedOut = new Promise((resolve) => {
    timer = setTimeout(() => resolve(null), CAMPAIGN_READ_TIMEOUT_MS);
  });
  try {
    return await Promise.race([read, timedOut]);
  } finally {
    clearTimeout(timer);
  }
}

/** Where a first-time approval's server status sends the admin, if anywhere. */
function approvalDestination(status) {
  if (status === 9) return "/coming-soon-campaign";
  if (status === 3) return "/active-campaign";
  return undefined;
}

/**
 * For an approval that was SAVED but whose outcome could not be read: retry
 * only the read (never the decision) and say where it landed.
 */
export async function checkApprovalOutcome(campaignId) {
  const status = await readCampaignStatus(campaignId);
  const navigateTo = approvalDestination(status);
  return navigateTo ? { status, navigateTo } : { status };
}

/**
 * `launchDate` is the API's Toronto wall-clock string from toApiDateTime. Its
 * PRESENCE is the scheduling intent: given, it must be usable (goLiveError) or
 * nothing is sent, so a value that failed to convert can never fall through to
 * a publish-now request. Omitted, the body is unchanged and the campaign
 * publishes now. The server's resolveApprovalSchedule makes a future date
 * Coming soon (9) and anything else live (3), and that decision, read back
 * afterwards, is what the operator is told and where they are sent.
 *
 * `contentRevision` is the reviewed version (C2). A refusal for it comes back
 * as `{ success: false, staleReview: message }`.
 */
export async function approveCampaignInitial(campaignId, options = {}) {
  const { comment, productId, launchDate, contentRevision } = options;
  const scheduling = Object.prototype.hasOwnProperty.call(options, "launchDate");
  if (scheduling) {
    const problem = goLiveError(launchDate);
    if (problem) {
      Swal.fire({
        title: "Choose another go-live time",
        text: problem,
        icon: "error",
        confirmButtonColor: "#f27474",
        confirmButtonText: "OK",
        showCloseButton: true,
      });
      return { success: false };
    }
  }
  const data = {
    campaign_id: campaignId,
    campaign_status: 3,
    reason: comment,
    product_type: productId,
    content_revision: contentRevision,
  };
  if (scheduling) data.launchDate = launchDate;
  let decided = false;
  let finalStatus = null;
  let stale = null;
  const confirmed = await confirmAction({
    title: "Approve this campaign?",
    text: scheduling
      // An hourly sweep promotes it at its first run at or after that time.
      ? `The campaign will be shown as Coming soon and go live within the hour after ${readableLaunch(launchDate)}.`
      : "The campaign will go live now.",
    confirmButtonText: "Yes, approve",
    preConfirm: async () => {
      // The dialog may have sat open past the lead time: check again at the
      // moment of sending, or the API would publish it now.
      if (scheduling) {
        const late = goLiveError(launchDate);
        if (late) {
          Swal.showValidationMessage(late);
          return;
        }
      }
      try {
        await axios({
          method: "post",
          url: API + "update-campaign-byadmin",
          headers: headers(),
          data,
        });
      } catch (error) {
        // Recorded, not shown here: the dialog closes and the panel offers Reload.
        stale = staleReviewRefusal(error);
        if (stale) return;
        Swal.showValidationMessage(
          SCHEDULE_ERROR_COPY[error?.response?.data?.code] || error?.response?.data?.message || "Failed to approve"
        );
        return;
      }
      decided = true;
      finalStatus = await readCampaignStatus(campaignId);
    },
  });
  if (stale) return { success: false, staleReview: stale };
  if (!confirmed || !decided) return { success: false };
  if (finalStatus === 9) {
    Swal.fire({
      title: "Approved and scheduled",
      text: `It goes live within the hour after ${readableLaunch(launchDate)}.`,
      icon: "success",
      confirmButtonText: "OK",
      showCloseButton: true,
    });
    return { success: true, status: 9, navigateTo: "/coming-soon-campaign" };
  }
  if (finalStatus === 3) {
    if (scheduling) {
      // The server published it although a date was chosen: say so, and leave
      // it on screen until it has been read.
      Swal.fire({
        title: "Approved Successfully",
        text: "The chosen time had passed, so it went live now.",
        icon: "success",
        confirmButtonText: "OK",
        showCloseButton: true,
      });
    } else {
      showSuccess("Approved Successfully");
    }
    return { success: true, status: 3, navigateTo: "/active-campaign" };
  }
  // The approval was saved but its outcome could not be read (failed, timed
  // out, or neither 3 nor 9): claim neither outcome and send the operator
  // nowhere. `outcomeUnknown` tells the panel to latch the decision and offer a
  // read-only "Check status" (checkApprovalOutcome).
  Swal.fire({
    title: "Approved",
    text: "The approval was saved, but the campaign's new status could not be read back. "
      + "Check the Active and Upcoming lists.",
    icon: "warning",
    confirmButtonText: "OK",
    showCloseButton: true,
  });
  return { success: true, status: finalStatus, outcomeUnknown: true };
}

export async function rejectCampaignInitial(campaignId, { comment, contentRevision } = {}) {
  const reasonError = rejectionReasonError(comment);
  if (reasonError) {
    showRejectionReasonRefusal(reasonError);
    return { success: false };
  }
  let refusal = null;
  let stale = null;
  const confirmed = await confirmAction({
    title: "Reject this campaign?",
    text: "This will reject the campaign. This action can affect issuer onboarding timelines.",
    confirmButtonText: "Yes, reject",
    isDanger: true,
    preConfirm: async () => {
      try {
        await axios({
          method: "post",
          url: API + "update-campaign-byadmin",
          headers: headers(),
          data: {
            campaign_id: campaignId,
            campaign_status: 2,
            reason: comment,
            content_revision: contentRevision,
          },
        });
      } catch (error) {
        // Recorded, not fired here: a Swal.fire inside preConfirm would replace
        // the open dialog mid-flight. Shown once the dialog has closed.
        stale = staleReviewRefusal(error);
        if (stale) return;
        refusal = rejectionReasonRefusal(error);
        if (!refusal) Swal.showValidationMessage(error?.response?.data?.message || "Failed to reject");
      }
    },
  });
  if (stale) return { success: false, staleReview: stale };
  if (refusal) {
    showRejectionReasonRefusal(refusal);
    return { success: false };
  }
  if (!confirmed) return { success: false };
  showSuccess("Rejected Successfully");
  return { success: true, navigateTo: "/rejected-campaign" };
}

// ---------------------------------------------------------------------------
// Workflow 2: Draft Review Approval (admin/approve-admin-review)
//   raiseCapital.controller.js:5825 — applies drafts + launch date logic
//   Can reclassify APPROVED → COMMING_SOON if launchDate is in the future
// ---------------------------------------------------------------------------

export async function approveCampaignReview(campaignId, {
  slug, userId, productId, previewData, contentRevision,
} = {}) {
  let stale = null;
  const confirmed = await confirmAction({
    title: "Approve this campaign?",
    text: "This will re-approve the campaign after review and make it visible to users.",
    confirmButtonText: "Yes, approve",
    preConfirm: async () => {
      try {
        await axios({
          method: "post",
          url: API + "admin/approve-admin-review",
          headers: headers(),
          data: {
            slug,
            user_id: userId,
            campaign_id: campaignId,
            status: 3,
            product_type: productId,
            content_revision: contentRevision,
          },
        });
      } catch (error) {
        // Recorded (see approveCampaignInitial); the dialog then closes as
        // confirmed, so this must be checked before any success is reported.
        stale = staleReviewRefusal(error);
        if (!stale) Swal.showValidationMessage(error?.response?.data?.message || "Failed to approve");
      }
    },
  });
  if (stale) return { success: false, staleReview: stale };
  if (!confirmed) return { success: false };
  showSuccess("Approved Successfully");

  // Draft review can reclassify to COMMING_SOON based on launch date
  const launchDate = previewData?.[0]?.step_seven?.launchDate;
  if (launchDate && new Date(launchDate) > new Date()) {
    return { success: true, navigateTo: "/coming-soon-campaign" };
  }
  return { success: true, navigateTo: "/active-campaign" };
}

export async function rejectCampaignReview(campaignId, { slug, userId, comment, contentRevision } = {}) {
  // A rejection without a reason is no longer allowed (the API refuses it with
  // 400 REJECTION_REASON_REQUIRED): the issuer would be told "Rejected" and
  // never why. So the dialog has one outcome to describe.
  const reasonError = rejectionReasonError(comment);
  if (reasonError) {
    showRejectionReasonRefusal(reasonError);
    return { success: false };
  }
  let refusal = null;
  let stale = null;
  const confirmed = await confirmAction({
    title: "Reject this campaign?",
    text: "This will reject the campaign and show the issuer your reason.",
    confirmButtonText: "Yes, reject",
    isDanger: true,
    preConfirm: async () => {
      try {
        await axios({
          method: "post",
          url: API + "admin/approve-admin-review",
          headers: headers(),
          data: {
            slug,
            user_id: userId,
            campaign_id: campaignId,
            status: 2,
            // F-2b (#26): persisted to campaign_details.rejection_reason and
            // rendered back to the issuer in My Campaigns and all three edit
            // surfaces. This workflow posted no comment field at all before,
            // which is the whole reason a rejected issuer was never told why.
            comment,
            content_revision: contentRevision,
          },
        });
      } catch (error) {
        // Recorded, not fired here (see rejectCampaignInitial).
        stale = staleReviewRefusal(error);
        if (stale) return;
        refusal = rejectionReasonRefusal(error);
        if (!refusal) Swal.showValidationMessage(error?.response?.data?.message || "Failed to reject");
      }
    },
  });
  if (stale) return { success: false, staleReview: stale };
  if (refusal) {
    showRejectionReasonRefusal(refusal);
    return { success: false };
  }
  if (!confirmed) return { success: false };
  showSuccess("Rejected Successfully");
  return { success: true, navigateTo: "/rejected-campaign" };
}

// ---------------------------------------------------------------------------
// Workflow 3: Post-Publication Transitions (update-campaign-status)
//   superAdmin.controller.js:1428 — pause / close / reactivate
// ---------------------------------------------------------------------------

export async function pauseCampaign(campaignId) {
  const confirmed = await confirmAction({
    title: "Pause this campaign?",
    text: "The campaign will be hidden from new investors until it is reactivated.",
    confirmButtonText: "Yes, pause",
    preConfirm: async () => {
      try {
        await axios({
          method: "post",
          url: API + "update-campaign-status",
          headers: headers(),
          data: { campaign_id: campaignId, campaign_status: 8 },
        });
      } catch (error) {
        Swal.showValidationMessage(error?.response?.data?.message || "Failed to pause");
      }
    },
  });
  if (!confirmed) return { success: false };
  showSuccess("Campaign Paused Successfully");
  return { success: true, navigateTo: "/paused-campaign" };
}

export async function closeCampaign(campaignId) {
  const confirmed = await confirmAction({
    title: "Close this campaign?",
    text: "Closing is permanent — the campaign will move to the closed queue and cannot be reactivated.",
    confirmButtonText: "Yes, close",
    isDanger: true,
    preConfirm: async () => {
      try {
        await axios({
          method: "post",
          url: API + "update-campaign-status",
          headers: headers(),
          data: { campaign_id: campaignId, campaign_status: 7 },
        });
      } catch (error) {
        Swal.showValidationMessage(error?.response?.data?.message || "Failed to close");
      }
    },
  });
  if (!confirmed) return { success: false };
  showSuccess("Campaign Closed Successfully");
  return { success: true, navigateTo: "/closed-campaign" };
}

export async function reactivateCampaign(campaignId) {
  const confirmed = await confirmAction({
    title: "Reactivate this campaign?",
    text: "This will make the campaign active and visible to users again.",
    confirmButtonText: "Yes, reactivate",
    preConfirm: async () => {
      try {
        await axios({
          method: "post",
          url: API + "update-campaign-status",
          headers: headers(),
          data: { campaign_id: campaignId, campaign_status: 3 },
        });
      } catch (error) {
        Swal.showValidationMessage(error?.response?.data?.message || "Failed to reactivate");
      }
    },
  });
  if (!confirmed) return { success: false };
  showSuccess("Campaign Reactivated Successfully");
  return { success: true, navigateTo: "/active-campaign" };
}

/**
 * reopenCompletedCampaign — OWNER DECISION 4 (2026-08-09).
 *
 * A COMPLETED campaign cannot go straight back to APPROVED: it would be live the
 * same instant, because it necessarily carries a launchDate in the past. The
 * route back is COMMING_SOON (9) with a NEW FUTURE launch date, after which it
 * goes live the ordinary way when the activation cron reaches that date.
 *
 * The server refuses the alternatives with named 409s
 * (COMPLETED_REAPPROVAL_MUST_BE_SCHEDULED, RELAUNCH_DATE_REQUIRED,
 * RELAUNCH_DATE_MUST_BE_FUTURE) — this dialog exists so an admin does not have
 * to discover them, not as the enforcement. The date input is bounded to
 * tomorrow onward for the same reason.
 *
 * The previous closing, refunds, payouts and investment history are untouched:
 * nothing here writes them, and campaign_closings is keyed by a UNIQUE
 * campaign_id, so a relaunch cannot overwrite the prior closing record.
 */
export async function reopenCompletedCampaign(campaignId) {
  const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

  const confirmed = await confirmAction({
    title: "Re-open this completed campaign?",
    text:
      "It returns as Coming Soon and goes live on the date you choose. "
      + "The previous closing and its investment history are kept and unchanged.",
    confirmButtonText: "Re-open",
    input: "date",
    inputLabel: "New launch date (must be in the future)",
    inputAttributes: { min: tomorrow },
    inputValidator: (value) => {
      if (!value) return "A new launch date is required";
      if (value < tomorrow) return "The new launch date must be in the future";
      return undefined;
    },
    preConfirm: async (launchDate) => {
      try {
        await axios({
          method: "post",
          url: API + "update-campaign-status",
          headers: headers(),
          data: {
            campaign_id: campaignId,
            campaign_status: 9, // COMMING_SOON — never 3
            launchDate,
          },
        });
      } catch (error) {
        const code = error?.response?.data?.code;
        const named = {
          COMPLETED_REAPPROVAL_MUST_BE_SCHEDULED:
            "A completed campaign cannot be approved directly. Re-open it as Coming Soon with a future launch date.",
          RELAUNCH_DATE_REQUIRED: "A new launch date is required.",
          RELAUNCH_DATE_MUST_BE_FUTURE: "The new launch date must be in the future.",
          COMPLETED_TRANSITION_NOT_ALLOWED: "A completed campaign can only be re-opened as Coming Soon.",
        }[code];
        Swal.showValidationMessage(named || error?.response?.data?.message || "Failed to re-open");
      }
    },
  });
  if (!confirmed) return { success: false };
  showSuccess("Campaign Re-opened — it will go live on the date you set");
  // QA 2026-09-07: "comming" pointed at a route that does not exist (404 under a success modal).
  return { success: true, navigateTo: "/coming-soon-campaign" };
}

// ---------------------------------------------------------------------------
// Admin CRUD Operations
// ---------------------------------------------------------------------------

export async function deleteCampaign(campaignId) {
  const confirmed = await confirmAction({
    title: "Are you sure?",
    text: "You won't be able to revert this!",
    confirmButtonText: "Yes, delete it!",
  });
  if (!confirmed) return { success: false };

  try {
    await axios({
      method: "patch",
      url: API + "admin/delete-campaign",
      headers: headers(),
      data: { campaign_id: campaignId },
    });
    Swal.fire("Deleted!", "Your file has been deleted.", "success");
    return { success: true, navigateTo: "/deleted-campaign" };
  } catch (error) {
    showError(error);
    return { success: false };
  }
}

export async function restoreCampaign(campaignId) {
  const confirmed = await confirmAction({
    title: "Are you sure?",
    text: "You won't be able to revert this!",
    confirmButtonText: "Yes, restore it!",
  });
  if (!confirmed) return { success: false };

  try {
    const res = await axios({
      method: "patch",
      url: API + "admin/delete-campaign",
      headers: headers(),
      data: { campaign_id: campaignId, is_deleted: 0 },
    });
    Swal.fire("Restore!", "Your campaign has been restored.", "success");
    // QA 2026-09-07: the API now says where the row landed (a never-submitted
    // draft returns to DRAFT, a submission to New Submissions, a previously
    // published campaign to For Review). It used to hard-code For Review, where a
    // never-published restore is not listed.
    const landed = res?.data?.data || {};
    const status = Number(landed.campaign_status);
    let navigateTo = "/deleted-campaign";
    if (status === 6) navigateTo = "/draft-campaign";
    else if (status === 1) navigateTo = Number(landed.isForAdminReview) === 1 ? "/for-review-campaign" : "/initial-approve";
    return { success: true, navigateTo };
  } catch (error) {
    showError(error);
    return { success: false };
  }
}

export async function permanentlyDeleteCampaign(campaignId) {
  const confirmed = await confirmAction({
    title: "Are you sure?",
    text: "This will permanently delete the campaign.",
    confirmButtonText: "Yes, delete it!",
    isDanger: true,
  });
  if (!confirmed) return { success: false };

  try {
    await axios({
      method: "delete",
      url: API + "admin/permanently-delete-campaign",
      headers: { ...headers(), "Content-Type": "application/json" },
      data: { campaign_id: campaignId },
    });
    Swal.fire("Deleted!", "Campaign has been permanently deleted.", "success");
    return { success: true, refresh: true };
  } catch (error) {
    Swal.fire({
      title: error?.response?.data?.message || "Failed to delete",
      timerProgressBar: true,
      icon: "error",
      confirmButtonText: "OK",
      showCloseButton: true,
    });
    return { success: false };
  }
}

export async function updateCampaignType(campaignId, campaignType) {
  try {
    await axios({
      method: "post",
      url: API + "admin/update-campaign-type",
      headers: headers(),
      data: { campaign_id: campaignId, campaign_type: campaignType },
    });
    showSuccess("Campaign type updated");
    return { success: true };
  } catch (error) {
    showError(error);
    return { success: false };
  }
}

/**
 * Set a campaign's start and/or end date WITHOUT changing its status.
 * QA 2026-07-31 §B.4.
 *
 * Replaces markComingSoon for the date-editing case. The two are genuinely
 * different actions and were only ever identical in the UI: this one leaves
 * campaign_status and publishedAt alone, whereas mark-campaign-to-coming-soon
 * nulls publishedAt and takes a live offering off the market (which is why the
 * API audits that one as UNPUBLISH). See withdrawAndReschedule below.
 *
 * A key OMITTED leaves the column untouched; passing null clears it. That is
 * what lets the end date be set independently of the start.
 *
 * Returns { success, data }. Deliberately NO navigateTo — a date edit should not
 * move the admin off the page they are working on; the caller refetches.
 */
export async function updateCampaignSchedule(campaignId, {
  launchDate,
  endDate,
  currentEndDate,
  campaignName,
  campaignStatus,
  confirmShorten = false,
} = {}) {
  const body = { campaign_id: campaignId };
  if (launchDate !== undefined) body.launchDate = launchDate;
  if (endDate !== undefined) body.campaing_end_date = endDate;
  if (confirmShorten) body.confirm_shorten = true;

  const isLive = Number(campaignStatus) === 3;
  const shortening = endDate && isShortening(currentEndDate, endDate);

  let saved = null;
  const confirmed = await confirmAction({
    title: shortening && isLive ? "Close this campaign earlier?" : "Update this campaign's schedule?",
    text: shortening && isLive
      ? `${campaignName || "This campaign"} is live and accepting investment. Moving the end date from `
        + `${toDateInputValue(currentEndDate)} to ${toDateInputValue(endDate)} decides investors' money `
        + `${Math.abs(daysBetween(currentEndDate, endDate))} days sooner — the minimum-or-refund outcome `
        + `is determined on the new date, not the old one.`
      : "The campaign's start and end dates will be updated. Every change is recorded in the audit log.",
    confirmButtonText: shortening && isLive ? "Yes, shorten the campaign" : "Save schedule",
    isDanger: !!(shortening && isLive),
    preConfirm: async () => {
      try {
        const res = await axios({
          method: "post",
          url: API + "update-campaign-schedule",
          headers: headers(),
          data: shortening && isLive ? { ...body, confirm_shorten: true } : body,
        });
        // Gate on the BODY, not on the absence of a throw. QA 2026-07-31 found
        // mark-campaign-to-coming-soon answering HTTP 200 with success:false on
        // a refusal, so this module reported "scheduled successfully" and
        // navigated to a queue the campaign was not in. The API now returns a
        // real 409 — this check costs one line and makes that class impossible.
        if (res?.data?.success !== true) {
          Swal.showValidationMessage(
            SCHEDULE_ERROR_COPY[res?.data?.code] || res?.data?.message || "The schedule could not be saved."
          );
          return;
        }
        saved = res.data.data;
      } catch (error) {
        const code = error?.response?.data?.code;
        Swal.showValidationMessage(
          SCHEDULE_ERROR_COPY[code] || error?.response?.data?.message || "The schedule could not be saved."
        );
      }
    },
  });

  if (!confirmed || !saved) return { success: false };
  showSuccess("Schedule updated");
  return { success: true, data: saved };
}

/**
 * Take a campaign off the market and relaunch it on a future date.
 *
 * This is what markComingSoon always did — the endpoint sets
 * campaign_status = COMMING_SOON and publishedAt = null — but it fired from a
 * plain modal with NO confirmation, so an admin adjusting a date could delist a
 * live offering without being told. On an APPROVED campaign the API records it
 * as UNPUBLISH; on an upcoming one, RESCHEDULE.
 */
export async function withdrawAndReschedule(campaignId, launchDate, { campaignName, campaignStatus } = {}) {
  const isLive = Number(campaignStatus) === 3;
  let ok = false;
  const confirmed = await confirmAction({
    title: isLive ? "Take this live campaign off the market?" : "Change the start date?",
    text: isLive
      ? `${campaignName || "This campaign"} is published and visible to investors right now. Setting a start `
        + `date withdraws it from the market immediately — its published date is cleared and it stops `
        + `accepting investment — and it returns on ${toDateInputValue(launchDate)}.`
      : `The campaign will launch on ${toDateInputValue(launchDate)}.`,
    confirmButtonText: isLive ? "Yes, withdraw and reschedule" : "Set start date",
    isDanger: isLive,
    preConfirm: async () => {
      try {
        const res = await axios({
          method: "post",
          url: API + "mark-campaign-to-coming-soon",
          headers: headers(),
          data: { campaign_id: campaignId, launchDate },
        });
        if (res?.data?.success !== true) {
          Swal.showValidationMessage(
            SCHEDULE_ERROR_COPY[res?.data?.code] || res?.data?.message || "The start date could not be set."
          );
          return;
        }
        ok = true;
      } catch (error) {
        const code = error?.response?.data?.code;
        Swal.showValidationMessage(
          SCHEDULE_ERROR_COPY[code] || error?.response?.data?.message || "The start date could not be set."
        );
      }
    },
  });

  if (!confirmed || !ok) return { success: false };
  showSuccess("Campaign scheduled");
  return { success: true, navigateTo: "/coming-soon-campaign" };
}

export async function shareCampaign(email, campaignId) {
  try {
    await axios({
      method: "post",
      url: API + "export-campaign",
      headers: headers(),
      data: { email, campaign_id: campaignId },
    });
    showSuccess("Campaign shared successfully");
    return { success: true };
  } catch (error) {
    showError(error);
    return { success: false };
  }
}

export async function fetchCampaignHistory(campaignId) {
  try {
    const response = await axios({
      method: "post",
      url: API + "admin/getAllLogsofCamapignbyId",
      headers: headers(),
      data: { campaign_id: campaignId },
    });
    return response.data?.data || [];
  } catch (error) {
    console.error("Failed to fetch campaign history", error);
    return [];
  }
}

export async function fetchCampaignProducts(type) {
  try {
    const response = await axios({
      method: "post",
      url: API + "admin/get-campaign-products",
      headers: headers(),
      data: { type },
    });
    return response.data?.data || [];
  } catch (error) {
    console.error("Failed to fetch campaign products", error);
    return [];
  }
}
